import { Box, Divider, Grid, IconButton, styled, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography, FormControl, InputLabel, Select, MenuItem, Chip, FormHelperText, ListItemText, Checkbox } from "@mui/material";
import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../features/authSlice";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import theme from "../../theme";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import { createProject, deleteProject, getProjectList, updateProject } from "../../api/projectApi";
import { Add, BorderColor, Close, DeleteForever, Description } from "@mui/icons-material";
import CommonButton from "../../common/components/CommonButton";
import CommonDialog from "../../common/components/CommonDialog";
import { ProficiencyLevels, ProjectCategory, ProjectTypes } from "../../common/const";
import CommonSelect from "../../common/components/CommonSelect";
import CommonFormField from "../../common/components/CommonFormField";
import Dropzone from "react-dropzone";
import RichTextEditor from "react-rte";
import CommonEditor from "../../common/components/CommonEditor";

const Cell = styled(TableCell)(({ theme }) => ({
    fontWeight: 600,
    padding: 5,
    textAlign: 'center',
    fontSize: 13,
    borderRight: `1px solid ${theme.palette.background.secondary}`,
    background: theme.palette.secondary.main,
    color: theme.palette.primary.main
}));

const InnerCell = styled(TableCell)(({ theme }) => ({
    padding: 10,
    textAlign: 'center',
    fontSize: 14,
    color: theme.palette.background.dark,
    borderRight: `1px solid ${theme.palette.background.secondary}`,
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 1,
    height: 'auto',
    maxHeight: '400px',
    overflow: 'auto',
}));

const ManageProject = () => {
    const dispatch = useDispatch();
    const { loading } = useSelector((state) => state.auth);
    const [projects, setProjects] = useState(null);
    const [pageNo, setPageNo] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingProject, setEditingProject] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [projectLevel, setProjectLevel] = useState('');
    const [fileNames, setFileNames] = useState([]);
    const [value, setValue] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchInput, setSearchInput] = useState('');

    // Validation error states
    const [projectNameError, setProjectNameError] = useState('');
    const [totalHoursError, setTotalHoursError] = useState('');
    const [domainError, setDomainError] = useState('');

    const [newProject, setNewProject] = useState({
        project_name: '',
        project_type: ProjectTypes[0].value,
        project_level: ProficiencyLevels[0].value,
        total_hours: '',
        description: '',
        category: [],
        goal: RichTextEditor?.createValueFromString('', 'html'),
    });

    // BUG - 1: Validate Project Name (must contain at least one letter, cannot be only numbers)
    const validateProjectName = (value) => {
        if (value.trim() === '') {
            setProjectNameError('');
            return true;
        }

        // Check if the value contains only numbers (no letters)
        const onlyNumbersRegex = /^[0-9\s]+$/;
        if (onlyNumbersRegex.test(value)) {
            setProjectNameError('Project name cannot contain only numbers. Please add some text.');
            return false;
        }

        // Check if value has at least one letter
        const hasLetter = /[A-Za-z]/.test(value);
        if (!hasLetter) {
            setProjectNameError('Project name must contain at least one letter');
            return false;
        }

        // Allow letters, numbers, spaces, and common punctuation
        const validCharsRegex = /^[A-Za-z0-9\s\-_,.!?()&]+$/;
        if (!validCharsRegex.test(value)) {
            setProjectNameError('Project name contains invalid special characters');
            return false;
        }

        setProjectNameError('');
        return true;
    };

    // BUG - 2: Validate Total Hours (must be greater than 0, cannot be 0)
    const validateTotalHours = (value) => {
        if (value === '' || value === null || value === undefined) {
            setTotalHoursError('');
            return true;
        }

        const hours = parseFloat(value);
        if (isNaN(hours)) {
            setTotalHoursError('Total hours must be a valid number');
            return false;
        }

        // BUG - 2 FIX: Prevent 0 value
        if (hours === 0) {
            setTotalHoursError('Total hours cannot be 0. Please enter a value greater than 0');
            return false;
        }

        if (hours < 0) {
            setTotalHoursError('Total hours cannot be negative');
            return false;
        }

        if (hours > 1000) {
            setTotalHoursError('Total hours cannot exceed 1000 hours');
            return false;
        }

        // Allow whole numbers or half numbers (0.5 increments)
        if (hours * 2 !== Math.floor(hours * 2)) {
            setTotalHoursError('Total hours can be whole numbers or half numbers (e.g., 1, 1.5, 2, 2.5)');
            return false;
        }

        setTotalHoursError('');
        return true;
    };

    // BUG - 3 & 4: Handle Domain Selection - Proper multi-select without duplicates
    const handleCategoryChange = (event) => {
        const value = event.target.value;
        // Handle both cases: when value is array or string
        const selectedValues = Array.isArray(value) ? value : (value ? value.split(',') : []);

        // Remove any potential duplicates
        const uniqueValues = [...new Set(selectedValues)];

        // Check if there were duplicates
        if (uniqueValues.length !== selectedValues.length && selectedValues.length > 0) {
            setDomainError('Duplicate domain selections are not allowed');
            showNotification('Duplicate domain selections are not allowed. Please select unique domains.', 'warning');
            // Use unique values anyway
            setNewProject((prev) => ({
                ...prev,
                category: uniqueValues
            }));
        } else {
            setDomainError('');
            setNewProject((prev) => ({
                ...prev,
                category: uniqueValues
            }));
        }
    };

    const getProjectData = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await getProjectList(pageNo + 1, pageSize, ProjectTypes[0].value, projectLevel, searchTerm);
            setProjects(response.data);
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    }

    useEffect(() => {
        getProjectData();
    }, [pageNo, pageSize, projectLevel, searchTerm]);

    const handleChangePage = (event, newPage) => {
        setPageNo(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setPageSize(parseInt(event.target.value, 10));
        setPageNo(0);
    };

    const handleDelete = async (id) => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await deleteProject(id);
            if (response.success) {
                showNotification(response.message, 'success');
                dispatch(setLoading({
                    loading: false
                }));
                setDeleteConfirm(null);
                getProjectData();
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    const handleOpenDialog = (project = null) => {
        setEditingProject(project);
        setProjectNameError('');
        setTotalHoursError('');
        setDomainError('');

        if (project) {
            setFileNames(project?.images || []);
            setNewProject({
                project_name: project?.project_name || '',
                project_type: project?.project_type || ProjectTypes[0].value,
                project_level: project?.project_level || ProficiencyLevels[0].value,
                total_hours: project?.total_hours || '',
                description: project?.description || '',
                category: project?.categories || [],
                goal: RichTextEditor?.createValueFromString(project?.goal || '', 'html')
            });
        } else {
            setFileNames([]);
            setNewProject({
                project_name: '',
                project_type: ProjectTypes[0].value,
                project_level: ProficiencyLevels[0].value,
                total_hours: '',
                description: '',
                category: [],
                goal: RichTextEditor?.createValueFromString('', 'html')
            });
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setEditingProject(null);
        setValue('');
        setProjectNameError('');
        setTotalHoursError('');
        setDomainError('');
        setNewProject({
            project_name: '',
            project_type: ProjectTypes[0].value,
            project_level: ProficiencyLevels[0].value,
            total_hours: '',
            description: '',
            category: [],
            goal: RichTextEditor?.createValueFromString('', 'html')
        });
        setFileNames([]);
        setOpenDialog(false);
    };

    const handleSaveProject = async () => {
        // Validate all fields before saving
        const isNameValid = validateProjectName(newProject.project_name);
        const isHoursValid = validateTotalHours(newProject.total_hours);

        // BUG - 1: Check project name is not empty
        if (!newProject.project_name.trim()) {
            showNotification('Please enter a project name', 'error');
            return;
        }

        if (!isNameValid) {
            showNotification(projectNameError, 'error');
            return;
        }

        // BUG - 2: Check total hours is provided and valid (not zero)
        if (!newProject.total_hours) {
            showNotification('Please enter total hours', 'error');
            return;
        }

        if (!isHoursValid) {
            showNotification(totalHoursError, 'error');
            return;
        }

        // BUG - 3 & 4: Check for duplicate domains before submission
        const categories = newProject.category || [];
        const hasDuplicates = categories.length !== new Set(categories).size;
        if (hasDuplicates) {
            showNotification('Duplicate domain selections are not allowed. Please remove duplicate domains.', 'error');
            return;
        }

        if (!newProject.description.trim()) {
            showNotification('Please enter a description', 'error');
            return;
        }

        if (fileNames.length === 0 && (!editingProject || !editingProject.images)) {
            showNotification('Please upload a project image', 'error');
            return;
        }

        try {
            dispatch(setLoading({
                loading: true
            }));
            let response;
            if (editingProject?.project_bank_id) {
                const formData = new FormData();
                formData.append('project_name', newProject?.project_name);
                formData.append('project_level', newProject?.project_level);
                formData.append('project_type', newProject?.project_type);
                formData.append('total_hours', newProject?.total_hours);
                formData.append('categories', JSON.stringify(newProject?.category));
                formData.append('description', newProject?.description);
                if (fileNames.length > 0 && fileNames[0] instanceof File) {
                    formData.append('images', fileNames[0]);
                }
                formData.append('goal', newProject.goal?._cache?.html);
                response = await updateProject(editingProject.project_bank_id, formData);
            } else {
                const formData = new FormData();
                formData.append('project_name', newProject?.project_name);
                formData.append('project_level', newProject?.project_level);
                formData.append('project_type', newProject?.project_type);
                formData.append('total_hours', newProject?.total_hours);
                formData.append('categories', JSON.stringify(newProject?.category));
                formData.append('description', newProject?.description);
                formData.append('images', fileNames[0]);
                formData.append('goal', newProject.goal?._cache?.html);
                response = await createProject(formData);
            }
            if (response.success) {
                showNotification(response.message, 'success');
                dispatch(setLoading({
                    loading: false
                }));
                setFileNames([]);
                getProjectData();
            }
            handleCloseDialog();
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    const handleDrop = acceptedFiles => {
        setFileNames(acceptedFiles?.map(files => files));
    };

    // Search functionality
    const handleSearch = () => {
        setSearchTerm(searchInput);
        setPageNo(0);
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };

    const handleClearSearch = () => {
        setSearchInput('');
        setSearchTerm('');
        setPageNo(0);
    };

    // Add this function in your component (around line 150)
    const handleRemoveDomain = (domainToRemove, event) => {
        // Stop event propagation to prevent any unwanted behavior
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }

        const updatedCategories = (newProject.category || []).filter(cat => cat !== domainToRemove);
        setNewProject((prev) => ({
            ...prev,
            category: updatedCategories
        }));
        setDomainError('');

        // Optional: Show a success message
        showNotification(`Domain removed successfully`, 'info');
    };

    return (
        <Fragment>
            {loading && (
                <LoadingSpinner />
            )}
            <CommonDialog
                open={openDialog}
                size="sm"
                padding="0px"
                title={
                    <Fragment>
                        <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
                            <Typography sx={{ fontWeight: 600, fontSize: 18 }}>{editingProject ? 'Edit Project' : 'Add New Project'}</Typography>
                            <Close
                                sx={{
                                    color: theme.palette.background.dark, fontSize: 20,
                                    float: 'right',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    handleCloseDialog();
                                }}
                            />
                        </Box>
                        <Divider />
                    </Fragment>
                }
                content={
                    <Grid container spacing={2} mt={0.1} mb={1}>
                        <Grid item sm={12}>
                            <Box sx={{ width: '100%' }}>
                                {fileNames?.length > 0 && fileNames[0] instanceof File ? (
                                    <Box sx={{ mt: 2 }}>
                                        {fileNames.map((file, index) => (
                                            <Box
                                                key={index}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    p: 2,
                                                    border: '1px solid',
                                                    borderColor: 'grey.300',
                                                    borderRadius: 1,
                                                    mb: 1,
                                                    backgroundColor: 'background.paper'
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Description color="primary" />
                                                    <Box>
                                                        <Typography variant="body1">{file.name}</Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {(file.size / 1024).toFixed(2)} KB
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        const updatedFiles = [...fileNames];
                                                        updatedFiles.splice(index, 1);
                                                        setFileNames(updatedFiles);
                                                    }}
                                                    color="error"
                                                >
                                                    <Close />
                                                </IconButton>
                                            </Box>
                                        ))}
                                    </Box>
                                ) : fileNames?.filename ? (
                                    <Box sx={{ mt: 2 }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                p: 2,
                                                border: '1px solid',
                                                borderColor: 'grey.300',
                                                borderRadius: 1,
                                                mb: 1,
                                                backgroundColor: 'background.paper'
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Description color="primary" />
                                                <Box>
                                                    <Typography variant="body1">{fileNames.filename}</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {(fileNames.size / 1024).toFixed(2)} KB
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <IconButton
                                                size="small"
                                                onClick={() => setFileNames([])}
                                                color="error"
                                            >
                                                <Close />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                ) : (
                                    <Dropzone
                                        onDrop={handleDrop}
                                        accept={{
                                            'image/jpeg': ['.jpg', '.jpeg'],
                                            'image/png': ['.png']
                                        }}
                                        maxFiles={1}
                                    >
                                        {({ getRootProps, getInputProps, isDragActive }) => (
                                            <Box
                                                {...getRootProps()}
                                                sx={{
                                                    border: '2px dashed',
                                                    borderColor: '#D8DAEB',
                                                    borderRadius: 2,
                                                    padding: { xs: 3, md: 6 },
                                                    textAlign: 'center',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s ease',
                                                    minHeight: 100,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <input {...getInputProps()} />
                                                <img src="/Images/sound.png" alt="upload" />
                                                <Typography fontWeight={600} gutterBottom>
                                                    Drag and drop your files
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" gutterBottom>
                                                    Supported formats: JPG, JPEG, PNG
                                                </Typography>
                                                <CommonButton
                                                    label="Select File"
                                                    color="Black"
                                                    background="White"
                                                    border={`1px solid ${theme.palette.background.secondary}`}
                                                    width="120px"
                                                    height="40px"
                                                    marginBottom="0px"
                                                    marginTop="10px"
                                                />
                                            </Box>
                                        )}
                                    </Dropzone>
                                )}
                            </Box>
                        </Grid>
                        <Grid item sm={12}>
                            <CommonFormField
                                label="Project Name *"
                                name="project_name"
                                value={newProject?.project_name}
                                onChange={(e) => {
                                    const newValue = e.target.value;
                                    setNewProject((prev) => ({
                                        ...prev,
                                        project_name: newValue
                                    }));
                                    validateProjectName(newValue);
                                }}
                                placeholder="Enter Project Name"
                                error={!!projectNameError}
                                helperText={projectNameError}
                            />
                        </Grid>
                        <Grid item sm={12}>
                            <CommonFormField
                                label="Description *"
                                name="description"
                                value={newProject?.description}
                                onChange={(e) => setNewProject((prev) => ({
                                    ...prev,
                                    description: e.target.value
                                }))}
                                placeholder="Enter Description"
                                multiline
                                rows={3}
                            />
                        </Grid>
                        <Grid item sm={12}>
                            <CommonSelect
                                label="Project Level *"
                                name="projectlevel"
                                value={newProject?.project_level}
                                onChange={(e) => setNewProject((prev) => ({
                                    ...prev,
                                    project_level: e.target.value
                                }))}
                                placeholder='Select Project Level'
                                options={ProficiencyLevels}
                            />
                        </Grid>
                        <Grid item sm={12}>
                            <CommonSelect
                                label="Project Type *"
                                name="projectype"
                                value={newProject?.project_type}
                                onChange={(e) => setNewProject((prev) => ({
                                    ...prev,
                                    project_type: e.target.value
                                }))}
                                placeholder="Select Project Type"
                                options={ProjectTypes.filter((a) => a.value === 'DCP_PROJECT')}
                            />
                        </Grid>

                        <Grid item sm={12}>
                            <FormControl fullWidth error={!!domainError}>
                                <InputLabel id="category-multiple-label">Domain/Category *</InputLabel>
                                <Select
                                    labelId="category-multiple-label"
                                    id="category-multiple"
                                    multiple
                                    value={newProject?.category || []}
                                    onChange={handleCategoryChange}
                                    label="Domain/Category *"
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((value) => {
                                                const option = ProjectCategory.find(opt => opt.value === value);
                                                return (
                                                    <Chip
                                                        key={value}
                                                        label={option?.label || value}
                                                        size="small"
                                                        onDelete={(e) => {
                                                            e.stopPropagation();
                                                            handleRemoveDomain(value, e);
                                                        }}
                                                        onClick={(e) => e.stopPropagation()}
                                                        deleteIcon={<Close sx={{ fontSize: 14 }} />}
                                                    />
                                                );
                                            })}
                                        </Box>
                                    )}
                                    MenuProps={{
                                        PaperProps: {
                                            style: {
                                                maxHeight: 224,
                                            },
                                        },
                                    }}
                                >
                                    {ProjectCategory.map((option) => {
                                        const isSelected = (newProject?.category || []).includes(option.value);
                                        return (
                                            <MenuItem
                                                key={option.value}
                                                value={option.value}
                                            >
                                                <Checkbox checked={isSelected} disabled={isSelected} size="small" />
                                                <ListItemText primary={option.label} />
                                                {isSelected && (
                                                    <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                                                        ✓ Selected
                                                    </Typography>
                                                )}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                                <FormHelperText>
                                    {domainError || "Select one or more domains (each domain can only be selected once)"}
                                </FormHelperText>
                            </FormControl>
                        </Grid>

                        <Grid item sm={12}>
                            <CommonFormField
                                label='Total Hours *'
                                type="number"
                                name="total_hours"
                                value={newProject?.total_hours}
                                onChange={(e) => {
                                    const newValue = e.target.value;
                                    // Prevent entering values greater than 1000
                                    if (newValue && parseFloat(newValue) > 1000) {
                                        showNotification('Total hours cannot exceed 1000 hours', 'warning');
                                        return;
                                    }
                                    // Prevent entering 0
                                    if (newValue === '0') {
                                        showNotification('Total hours cannot be 0. Please enter a value greater than 0', 'warning');
                                        return;
                                    }
                                    setNewProject((prev) => ({
                                        ...prev,
                                        total_hours: newValue
                                    }));
                                    validateTotalHours(newValue);
                                }}
                                onBlur={(e) => {
                                    const value = e.target.value;
                                    if (value && parseFloat(value) > 1000) {
                                        setNewProject((prev) => ({
                                            ...prev,
                                            total_hours: 1000
                                        }));
                                        validateTotalHours(1000);
                                        showNotification('Total hours has been set to maximum allowed value (1000 hours)', 'info');
                                    }
                                    if (value === '0') {
                                        setNewProject((prev) => ({
                                            ...prev,
                                            total_hours: ''
                                        }));
                                        setTotalHoursError('Total hours cannot be 0');
                                    }
                                }}
                                placeholder='Enter Total Hours (0.5 - 1000 hours)'
                                error={!!totalHoursError}
                                helperText={totalHoursError || "Enter hours between 0.5 and 1000 (e.g., 0.5, 1, 1.5, 2, 2.5)"}
                                inputProps={{
                                    min: "0.5",
                                    max: "1000",
                                    step: "0.5"
                                }}
                            />
                        </Grid>
                        <Grid item sm={12}>
                            <CommonEditor
                                placeholder="Enter the goal"
                                value={newProject.goal}
                                setValue={(value) => {
                                    setValue(value.toString('html'));
                                    setNewProject({ ...newProject, goal: value })
                                }}
                            />
                        </Grid>
                    </Grid>
                }
                actionButtons={(
                    <CommonButton
                        label={editingProject ? 'Update Project' : 'Save Project'}
                        size="small"
                        background={theme.palette.secondary.main}
                        color={theme.palette.background.main}
                        fontWeight={700}
                        marginBottom="10px"
                        disabled={
                            !newProject?.project_name?.trim() ||
                            !!projectNameError ||
                            !newProject?.total_hours ||
                            !!totalHoursError ||
                            !newProject?.description?.trim() ||
                            (fileNames.length === 0 && !editingProject) ||
                            (newProject?.category || []).length === 0 ||
                            !!domainError
                        }
                        handleClick={() => {
                            handleSaveProject();
                        }}
                    />
                )}
            />
            <CommonDialog
                open={!!deleteConfirm}
                size="xs"
                content={(
                    <center>
                        <Typography sx={{ fontWeight: 600, fontSize: '22px', mt: 0.5, mb: 1 }}>
                            Are you sure you want to delete this project? This action cannot be undone.
                        </Typography>
                    </center>
                )}
                actionButtons={(
                    <Fragment>
                        <CommonButton
                            label="Yes"
                            size="small"
                            background={theme.palette.secondary.main}
                            color={theme.palette.primary.main}
                            handleClick={async () => {
                                handleDelete(deleteConfirm.project_bank_id)
                            }}
                        />
                        <CommonButton
                            label="No"
                            size="small"
                            background={theme.palette.background.main}
                            border={`1px solid ${theme.palette.background.light}`}
                            handleClick={() => {
                                setDeleteConfirm(null)
                            }}
                        />
                    </Fragment>
                )}
            />
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                <Typography fontSize={20} fontWeight={600}>Project List</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ width: '200px' }}>
                        <CommonSelect
                            name="projectlevel"
                            value={projectLevel}
                            onChange={(e) => setProjectLevel(e.target.value)}
                            placeholder='Select Project Level'
                            options={ProficiencyLevels}
                            allowClear
                        />
                    </Box>
                    <CommonButton
                        label="Add Project"
                        marginBottom="0px"
                        width="150px"
                        fontWeight={600}
                        startIcon={<Add />}
                        background={theme.palette.secondary.main}
                        color="White"
                        handleClick={() => handleOpenDialog()}
                    />
                </Box>
            </Box>
            <Box pt={1} pb={0}>
                <StyledTableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                                <Cell>Project Name</Cell>
                                <Cell>Level</Cell>
                                <Cell>Project Type</Cell>
                                <Cell>Total Hours</Cell>
                                <Cell>Actions</Cell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {projects?.data?.length > 0 ? projects?.data?.map((project) => (
                                <TableRow key={project.project_bank_id}>
                                    <InnerCell style={{ color: 'black' }}>
                                        {project.project_name}
                                    </InnerCell>
                                    <InnerCell>
                                        {project.project_level}
                                    </InnerCell>
                                    <InnerCell>
                                        {project.project_type === 'DCP_PROJECT' ? 'DCP Project' : 'External Project'}
                                    </InnerCell>
                                    <InnerCell>
                                        {project.total_hours}
                                    </InnerCell>
                                    <InnerCell>
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() => handleOpenDialog(project)}
                                        >
                                            <BorderColor sx={{ color: theme.palette.secondary.main, fontSize: 17 }} />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => setDeleteConfirm(project)}
                                        >
                                            <DeleteForever sx={{ color: 'red', fontSize: 20 }} />
                                        </IconButton>
                                    </InnerCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        <Typography>{searchTerm ? 'No projects found matching your search' : 'No Project Found'}</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </StyledTableContainer>
            </Box>

            {/* Pagination Component */}
            {projects?.pagination?.total > 0 && (
                <Box sx={{ borderTop: `1px solid ${theme.palette.grey[300]}` }}>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 15, 20, 25, 50]}
                        component="div"
                        count={projects?.pagination?.total}
                        rowsPerPage={pageSize}
                        page={pageNo}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{
                            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                fontSize: '0.875rem',
                            },
                        }}
                    />
                </Box>
            )}
        </Fragment>
    )
}

export default ManageProject;